import { getCachedLiveStreams, invalidateLiveStreamsCache } from '../utils/liveStreamsCache';
import { getCachedVodStreams, invalidateVodStreamsCache } from '../utils/vodStreamsCache';
import { getCachedSeriesStreams, invalidateSeriesStreamsCache } from '../utils/seriesStreamsCache';
import { XtreamContent } from '../api/xtreamAPI';

export type CatalogHashKind = 'live' | 'vod' | 'series';
export type CatalogRefreshState = 'waiting' | 'running' | 'done' | 'failed';

export interface CatalogHashEntry {
  hash: string;
  itemCount: number;
  updatedAt: number;
}

export interface CatalogHashSnapshot {
  identity: string;
  live?: CatalogHashEntry;
  vod?: CatalogHashEntry;
  series?: CatalogHashEntry;
}

export interface CatalogRefreshProgress {
  live: CatalogRefreshState;
  vod: CatalogRefreshState;
  series: CatalogRefreshState;
  activeKind?: CatalogHashKind;
  errorKind?: CatalogHashKind;
}

const HASH_TTL_MS = 24 * 60 * 60 * 1000;

function rotateRight(value: number, amount: number): number {
  return (value >>> amount) | (value << (32 - amount));
}

/** Small synchronous SHA-256 implementation for Chrome 47/Tizen 3. */
export function sha256(value: string): string {
  const bytes: number[] = [];
  for (let index = 0; index < value.length; index += 1) {
    let code = value.charCodeAt(index);
    if (code < 0x80) bytes.push(code);
    else if (code < 0x800) bytes.push(0xc0 | (code >> 6), 0x80 | (code & 63));
    else if (code >= 0xd800 && code <= 0xdbff && index + 1 < value.length) {
      const next = value.charCodeAt(++index);
      code = 0x10000 + ((code & 0x3ff) << 10) + (next & 0x3ff);
      bytes.push(0xf0 | (code >> 18), 0x80 | ((code >> 12) & 63), 0x80 | ((code >> 6) & 63), 0x80 | (code & 63));
    } else bytes.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 63), 0x80 | (code & 63));
  }
  const bitLength = bytes.length * 8;
  bytes.push(0x80);
  while (bytes.length % 64 !== 56) bytes.push(0);
  const high = Math.floor(bitLength / 0x100000000);
  const low = bitLength >>> 0;
  for (let shift = 24; shift >= 0; shift -= 8) bytes.push((high >>> shift) & 255);
  for (let shift = 24; shift >= 0; shift -= 8) bytes.push((low >>> shift) & 255);

  const constants = [
    0x428a2f98,0x71374491,0xb5c0fbcf,0xe9b5dba5,0x3956c25b,0x59f111f1,0x923f82a4,0xab1c5ed5,
    0xd807aa98,0x12835b01,0x243185be,0x550c7dc3,0x72be5d74,0x80deb1fe,0x9bdc06a7,0xc19bf174,
    0xe49b69c1,0xefbe4786,0x0fc19dc6,0x240ca1cc,0x2de92c6f,0x4a7484aa,0x5cb0a9dc,0x76f988da,
    0x983e5152,0xa831c66d,0xb00327c8,0xbf597fc7,0xc6e00bf3,0xd5a79147,0x06ca6351,0x14292967,
    0x27b70a85,0x2e1b2138,0x4d2c6dfc,0x53380d13,0x650a7354,0x766a0abb,0x81c2c92e,0x92722c85,
    0xa2bfe8a1,0xa81a664b,0xc24b8b70,0xc76c51a3,0xd192e819,0xd6990624,0xf40e3585,0x106aa070,
    0x19a4c116,0x1e376c08,0x2748774c,0x34b0bcb5,0x391c0cb3,0x4ed8aa4a,0x5b9cca4f,0x682e6ff3,
    0x748f82ee,0x78a5636f,0x84c87814,0x8cc70208,0x90befffa,0xa4506ceb,0xbef9a3f7,0xc67178f2,
  ];
  const hash = [0x6a09e667,0xbb67ae85,0x3c6ef372,0xa54ff53a,0x510e527f,0x9b05688c,0x1f83d9ab,0x5be0cd19];
  const words = new Array<number>(64);
  for (let offset = 0; offset < bytes.length; offset += 64) {
    for (let index = 0; index < 16; index += 1) {
      const pos = offset + index * 4;
      words[index] = ((bytes[pos] << 24) | (bytes[pos + 1] << 16) | (bytes[pos + 2] << 8) | bytes[pos + 3]) | 0;
    }
    for (let index = 16; index < 64; index += 1) {
      const x = words[index - 15];
      const y = words[index - 2];
      const s0 = rotateRight(x, 7) ^ rotateRight(x, 18) ^ (x >>> 3);
      const s1 = rotateRight(y, 17) ^ rotateRight(y, 19) ^ (y >>> 10);
      words[index] = (words[index - 16] + s0 + words[index - 7] + s1) | 0;
    }
    let [a,b,c,d,e,f,g,h] = hash;
    for (let index = 0; index < 64; index += 1) {
      const s1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25);
      const choose = (e & f) ^ (~e & g);
      const temp1 = (h + s1 + choose + constants[index] + words[index]) | 0;
      const s0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22);
      const majority = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (s0 + majority) | 0;
      h = g; g = f; f = e; e = (d + temp1) | 0; d = c; c = b; b = a; a = (temp1 + temp2) | 0;
    }
    hash[0]=(hash[0]+a)|0; hash[1]=(hash[1]+b)|0; hash[2]=(hash[2]+c)|0; hash[3]=(hash[3]+d)|0;
    hash[4]=(hash[4]+e)|0; hash[5]=(hash[5]+f)|0; hash[6]=(hash[6]+g)|0; hash[7]=(hash[7]+h)|0;
  }
  return hash.map((word) => (`00000000${(word >>> 0).toString(16)}`).slice(-8)).join('');
}

export function computeCatalogHash(kind: CatalogHashKind, items: XtreamContent[]): string {
  const entries = items.map((item) => {
    const rawId = kind === 'series' ? item.series_id : item.stream_id;
    const id = String(rawId == null ? '' : rawId).trim();
    return id ? `${id}\u001f${String(item.category_id == null ? '' : item.category_id).trim()}` : '';
  }).filter(Boolean).sort();
  return sha256(entries.join('\n'));
}

class CatalogHashService {
  private snapshot: CatalogHashSnapshot = { identity: '' };
  private storageKey = '';
  private readonly inFlight = new Map<CatalogHashKind, Promise<CatalogHashEntry>>();

  configure(identity: string): void {
    // Xtream usernames may be case-sensitive. The endpoint is normalized by the
    // caller, but the account portion must retain its exact case.
    const normalized = String(identity || '').trim();
    if (normalized === this.snapshot.identity) return;
    this.storageKey = `ott_catalog_hashes_v1_${sha256(normalized).slice(0, 24)}`;
    try {
      const parsed = JSON.parse(localStorage.getItem(this.storageKey) || 'null') as CatalogHashSnapshot | null;
      this.snapshot = parsed?.identity === normalized ? parsed : { identity: normalized };
    } catch {
      this.snapshot = { identity: normalized };
    }
  }

  getHash(kind: CatalogHashKind): string | null {
    return this.snapshot[kind]?.hash || null;
  }

  getEntry(kind: CatalogHashKind): CatalogHashEntry | undefined {
    return this.snapshot[kind];
  }

  clear(): void {
    if (this.storageKey) {
      try { localStorage.removeItem(this.storageKey); } catch { /* storage unavailable */ }
    }
    this.snapshot = { identity: this.snapshot.identity };
    this.inFlight.clear();
  }

  isStale(kind: CatalogHashKind): boolean {
    const entry = this.snapshot[kind];
    return !entry || Date.now() - entry.updatedAt >= HASH_TTL_MS;
  }

  private persist(): void {
    if (!this.storageKey) return;
    try { localStorage.setItem(this.storageKey, JSON.stringify(this.snapshot)); } catch { /* storage unavailable */ }
  }

  private async fetch(kind: CatalogHashKind, force: boolean): Promise<XtreamContent[]> {
    if (force) {
      if (kind === 'live') invalidateLiveStreamsCache();
      if (kind === 'vod') invalidateVodStreamsCache();
      if (kind === 'series') invalidateSeriesStreamsCache();
    }
    if (kind === 'live') return getCachedLiveStreams();
    if (kind === 'vod') return getCachedVodStreams();
    return getCachedSeriesStreams();
  }

  async ensure(kind: CatalogHashKind, force = false): Promise<CatalogHashEntry> {
    const existing = this.snapshot[kind];
    if (!force && existing && !this.isStale(kind)) return existing;
    const active = this.inFlight.get(kind);
    if (active) return active;
    const promise = this.fetch(kind, force).then((items) => {
      const entry = { hash: computeCatalogHash(kind, items), itemCount: items.length, updatedAt: Date.now() };
      this.snapshot = { ...this.snapshot, [kind]: entry };
      this.persist();
      return entry;
    }).finally(() => this.inFlight.delete(kind));
    this.inFlight.set(kind, promise);
    return promise;
  }

  async refreshSequential(
    force: boolean,
    onProgress?: (progress: CatalogRefreshProgress) => void,
    kinds: CatalogHashKind[] = ['live', 'vod', 'series'],
  ): Promise<CatalogRefreshProgress> {
    const progress: CatalogRefreshProgress = {
      live: kinds.indexOf('live') >= 0 ? 'waiting' : 'done',
      vod: kinds.indexOf('vod') >= 0 ? 'waiting' : 'done',
      series: kinds.indexOf('series') >= 0 ? 'waiting' : 'done',
    };
    const emit = () => onProgress?.({ ...progress });
    emit();
    for (const kind of kinds) {
      if (!force && !this.isStale(kind)) {
        progress[kind] = 'done'; emit(); continue;
      }
      progress.activeKind = kind;
      progress[kind] = 'running'; emit();
      try {
        await this.ensure(kind, force);
        progress[kind] = 'done';
      } catch {
        progress[kind] = 'failed';
        progress.errorKind = kind;
      }
      emit();
    }
    delete progress.activeKind;
    return progress;
  }
}

export const catalogHashService = new CatalogHashService();
